import { Injectable } from '@angular/core';
import { PagedSearch, PagedResult } from '../../../shared/index';
import { PagedResult as GenericPagedResult } from '../../../shared/model/generic-paged-result.model';
import { EventItemCreditType } from './event-item-credit-type.model';
import { EventItemAffCode } from './event-item-aff-code.model';
import { EventItemSummary } from './event-item-summary.model';
import { EventItemEdit } from './event-item-edit.model';
import { EventsServiceApiService } from 'src/app/shared/api/events-service-api.service';
import { IEventV4ItemService } from './ievent-v4-item.service';
import { EventItemDetails } from './event-item-details.model';
import { EventItemSearchDto } from './event-item-search.dto';

@Injectable({
  providedIn: 'root',
})
export class EventV4ItemService implements IEventV4ItemService {
  private _eventServiceBaseUrl: string = '/event/v2/item/';

  public constructor(private _eventApiService: EventsServiceApiService) {}

  private getEventItemsApiGetUrl = (eventId: number): string => {
    return this._eventServiceBaseUrl + 'event/' + eventId.toString();
  };

  public getEventItems = (eventId: number): Promise<Array<EventItemSummary>> => {
    if (!eventId) {
      return Promise.resolve([]);
    }

    let url: string = this.getEventItemsApiGetUrl(eventId);

    return this._eventApiService.get(url).then((data: PagedResult) => {
      return Promise.resolve(data.Result);
    });
  };

  public deleteEventItemsCache = (eventId: number): void => {
    let url: string = this.getEventItemsApiGetUrl(eventId);

    this._eventApiService.deleteCache(url);
  };

  public getEventItemToEdit = (eventItemId: number): Promise<EventItemEdit> => {
    let url: string = this._eventServiceBaseUrl + eventItemId.toString();
    return this._eventApiService.get(url).then((data: EventItemEdit) => {
      let eventItemToEdit = data;

      if (!eventItemToEdit.AffCodes) eventItemToEdit.AffCodes = [];

      if (!eventItemToEdit.AffCodeRestrictions) eventItemToEdit.AffCodeRestrictions = [];

      if (!eventItemToEdit.CreditTypes) eventItemToEdit.CreditTypes = [];

      for (let affCodeRes of data.AffCodeRestrictions) {
        eventItemToEdit.AffCodes.push(affCodeRes.AffCode);
      }

      return Promise.resolve(eventItemToEdit);
    });
  };

  public getNewEventItem = (eventId: number): Promise<EventItemEdit> => {
    let newEventItem = new EventItemEdit();
    newEventItem.EventId = eventId;
    newEventItem.AttendeesForItem = 1;
    newEventItem.IsPublic = true;
    return Promise.resolve(newEventItem);
  };

  public upsertEventItem = (eventItemEdit: EventItemEdit): Promise<number> => {
    let url: string = this._eventServiceBaseUrl + 'upsert';

    eventItemEdit.AffCodeRestrictions = new Array<EventItemAffCode>();

    if (eventItemEdit.RestrictForAffiliations) {
      for (let affCode of eventItemEdit.AffCodes) {
        let itemAffCode = new EventItemAffCode();
        itemAffCode.AffCode = affCode;
        itemAffCode.EventItemId = eventItemEdit.EventItemId;

        eventItemEdit.AffCodeRestrictions.push(itemAffCode);
      }
    }

    return this._eventApiService.post(url, eventItemEdit).then((data: number): Promise<number> => {
      return Promise.resolve(data);
    });
  };

  public copyEventItem = (eventItemEdit: EventItemEdit): Promise<number> => {
    let url: string = this._eventServiceBaseUrl + 'copy';

    return this._eventApiService.post(url, eventItemEdit).then((data: number): Promise<number> => {
      return Promise.resolve(data);
    });
  };

  public deleteEventItem = (eventItemId: number): Promise<any> => {
    let url: string = this._eventServiceBaseUrl + eventItemId;
    return this._eventApiService.delete(url);
  };

  public getEventItemCeus = (eventItemId: number): Promise<GenericPagedResult<Array<EventItemCreditType>>> => {
    let params: PagedSearch = new PagedSearch();
    params.PageSize = 0;
    params.PageNumber = 1;
    let url: string = `${this._eventServiceBaseUrl + eventItemId}/credit-types`;
    return this._eventApiService.getPaged(url, params);
  };

  public reorderEventItems(eventId: number, orderedEventItemIds: Array<number>): Promise<any> {
    let url: string = `${this._eventServiceBaseUrl}event/${eventId}/order-items`;
    return this._eventApiService.post(url, orderedEventItemIds);
  }

  public getEventItemDetails = (searchDto: EventItemSearchDto): Promise<GenericPagedResult<Array<EventItemDetails>>> => {
    let url: string = `${this._eventServiceBaseUrl}item-details`;
    return this._eventApiService.post(url, searchDto);
  };

  public exportEventItemDetails = (searchDto: EventItemSearchDto): Promise<string> => {
    let url: string = `${this._eventServiceBaseUrl}export-item-details`;
    return this._eventApiService.post(url, searchDto);
  };
}
